import React, {useCallback, useEffect, useState} from "react";
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Errors from "../../../utils/Errors";
import CloverCardDetailsField from './CloverCardDetailsField';
import {FormattedMessage, injectIntl, useIntl} from "react-intl";
import {Box, Button, Divider, Grid, Link, Typography, withStyles} from "@material-ui/core";
import StyleableSection from "../../../components/StyleableSection";
import CardDesignView from "../../../components/CardDesignView";
import formatMoney from "../../../utils/formatMoney";
import moment from "moment";
import DeliverTimes from "../../../constants/DeliverTimes";
import {red, grey} from "@material-ui/core/colors";
import CloverDevPayCardDetailsForm from "./CloverDevPayCardDetailsForm";
import SquareCardDetailsForm from "./SquareCardDetailsForm";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import CheckoutActions from "../../../constants/CheckoutActions";

const styles = (theme) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    maxWidth: 720,
  },
  title: {
    textAlign: 'center'
  },
  cardDetailsContainer: {
    width: '100%'
  },
  paymentRequestContainer: {
    width: 256,
    marginTop: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    height: '100%'
  },
  buttonContainer: {
    marginTop: 32,
    width: 256,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  testCardDetails: {
    marginTop: 16,
    borderRadius: 4,
    backgroundColor: grey[300],
    fontSize: 12,
    width: 240,
    padding: 8
  },
  testCardDetailsHeader: {
    height: 16,
    verticalAlign: 'text-top'
  },
  errorBanner: {
    width: '100%',
    backgroundColor: red[500],
  },
  errorBannerText: {
    fontSize: 12,
    color: 'white',
    padding: 4,
    paddingLeft: 8,
    paddingRight: 8,
    textAlign: 'center'
  },
  hidden: {
    display: 'none'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  orderSummaryHeader: {
    marginTop: theme.spacing(0.5)
  },
  orderSummaryContainer: {
    padding: theme.spacing(2)
  },
  orderSummaryEditButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  orderSummaryDivider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  termsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'flex-end'
  },
  termsCentered: {
    color: theme.palette.text.hint,
    textAlign: 'center'
  },
  link: {
    color: theme.palette.text.hint,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5)
  },
  amountYouPay: {
    fontWeight: 'bold'
  }
});

PaymentForm.propTypes = {
  stripePaymentConfig: PropTypes.object,
  cloverPaymentConfig: PropTypes.object,
  squarePaymentConfig: PropTypes.object,
  initializingCloverPaymentConfig: PropTypes.bool,
  initializingSquarePaymentConfig: PropTypes.bool,
  checkoutSettings: PropTypes.object.isRequired,
  amountDue: PropTypes.object.isRequired,
  currencyCode: PropTypes.string.isRequired,
  recipientName: PropTypes.string,
  recipientEmail: PropTypes.string,
  sendToSender: PropTypes.bool.isRequired,
  senderName: PropTypes.string,
  senderEmail: PropTypes.string,
  message: PropTypes.string,
  paymentToken: PropTypes.object,
  cardDesign: PropTypes.object.isRequired,
  amount: PropTypes.number.isRequired,
  balance: PropTypes.number.isRequired,
  deliveryTimeType: PropTypes.string.isRequired,
  deliveryDateTime: PropTypes.object,
  pricing: PropTypes.object.isRequired,
  action: PropTypes.string.isRequired,
  giftCardDetails: PropTypes.object,
  locale: PropTypes.string.isRequired,
  idempotencyKey: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  errors: PropTypes.instanceOf(Errors).isRequired,
  testMode: PropTypes.boolean,
  classes: PropTypes.object,
  onEditOrder: PropTypes.func.isRequired,
  onPay: PropTypes.func.isRequired
};

function PaymentForm({
                       cloverPaymentConfig,
                       squarePaymentConfig,
                       initializingCloverPaymentConfig,
                       initializingSquarePaymentConfig,
                       checkoutSettings,
                       amountDue,
                       cardDesign,
                       balance,
                       currencyCode,
                       recipientName,
                       recipientEmail,
                       sendToSender,
                       senderName,
                       senderEmail,
                       message,
                       deliveryTimeType,
                       deliveryDateTime,
                       pricing,
                       action,
                       giftCardDetails,
                       testMode = false,
                       idempotencyKey,
                       errors,
                       classes,
                       onEditOrder,
                       onPay,
                       visible,
                       locale = 'en_US',
                     }) {

  const [paymentErrors, setPaymentErrors] = useState(new Errors());
  const [paying, setPaying] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const intl = useIntl();

  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('checkout');
    return token;
  };

  function handleCloverPaymentTokenCreated(paymentToken) {
    setPaymentErrors(paymentErrors.clearAll());
    setPaying(true);
    return executeRecaptcha('checkout')
      .then((recaptchaToken) => {
        return onPay(paymentToken, recaptchaToken).then((order) => {
          setPaying(false);
        }).catch((errorResponse) => {
          setPaying(false);
          setPaymentErrors(paymentErrors.addError('payment', errorResponse.message));
        });
      });
  }

  function handleSquarePaymentTokenCreated(paymentToken) {
    setPaymentErrors(paymentErrors.clearAll());
    setPaying(true);
    return executeRecaptcha("checkout")
      .then((recaptchaToken) => {
        return onPay(paymentToken, recaptchaToken).then((order) => {
          setPaying(false);
        }).catch((errorResponse) => {
          setPaying(false);
          setPaymentErrors(paymentErrors.addError('payment', errorResponse.message));
        });
      });
  }

  function renderTestCardDetails() {
    if (testMode) {
      return (
        <div className={classes.testCardDetails}>
          <table>
            <thead>
            <tr className={classes.testCardDetailsHeader}>
              <td colSpan={2}>
                <FormattedMessage id={"pay.test-card-header"}/>
              </td>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td><FormattedMessage id={"pay.card"}/> #</td>
              <td>4242 4242 4242 4242</td>
            </tr>
            <tr>
              <td><FormattedMessage id={"pay.expiration"}/></td>
              <td>04/24</td>
            </tr>
            <tr>
              <td><FormattedMessage id={"pay.cvc"}/></td>
              <td>242</td>
            </tr>
            <tr>
              <td><FormattedMessage id={"pay.cvc"}/></td>
              <td>42424</td>
            </tr>
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  }

  function renderOrderSummary() {
    let showDeliverInstant = false;
    let showDeliverFuture = false;
    let showIncentive = false;
    if(action == CheckoutActions.NEW_CARD) {
      showDeliverInstant = deliveryTimeType === DeliverTimes.NOW;
      showDeliverFuture = deliveryTimeType === DeliverTimes.FUTURE;
    } else if(action === CheckoutActions.RELOAD_CARD) {
      showDeliverInstant = true;
      showDeliverFuture = false;
    }

    let lineItems = [];
    let giftAmount = balance;
    let reloadNewBalance = null;
    if(pricing) {
      if(action === CheckoutActions.NEW_CARD) {
        giftAmount = pricing.balance;
      } else if(action === CheckoutActions.RELOAD_CARD) {
        giftAmount = pricing.amount;
        if(pricing.incentiveBonusAmount) {
          giftAmount += pricing.incentiveBonusAmount;
        }
      }

      if(pricing.incentive) {
        lineItems.push({
          key: intl.formatMessage({id:'label.gift-amount'}),
          value: formatMoney(pricing.amount, currencyCode)
        });
        if (pricing.incentive.rewardee.type === 'RECIPIENT') {
          lineItems.push({
            key: intl.formatMessage({id:'label.bonus-amount'}),
            value: formatMoney(pricing.incentive.bonusAmount, currencyCode)
          });
        } else if(pricing.incentive.rewardee.type === 'PURCHASER') {
          lineItems.push({
            key: intl.formatMessage({id:'label.bonus-gift-card'}),
            value: formatMoney(pricing.incentive.bonusAmount, currencyCode)
          });
        }
      }
      if(action === CheckoutActions.RELOAD_CARD && giftCardDetails) {
        reloadNewBalance = formatMoney(pricing.balance, currencyCode);
      }
    }

    return (
      <>
        <Grid className={classes.orderSummaryHeader} container alignItems={'center'}>
          <Grid item xs={6}>
            <Typography variant={'button'}>
              <FormattedMessage id={'header.order-summary'}/>
            </Typography>
          </Grid>
          <Grid item xs={6} className={classes.orderSummaryEditButtonContainer}>
            <Button
              variant={'text'}
              color={'primary'}
              disabled={paying}
              onClick={onEditOrder}
            >
              <FormattedMessage id={'button.edit'}/>
            </Button>
          </Grid>
        </Grid>
        <StyleableSection section={checkoutSettings.orderSummary}>
          <StyleableSection section={checkoutSettings.header}>
            <CardDesignView
              locale={locale}
              cardDesign={cardDesign}
              size={'small'}
            />
          </StyleableSection>
          <Grid container className={classes.orderSummaryContainer} spacing={1}>
            <Grid item xs={12}>
              <Typography variant={'subtitle1'}>
                {showDeliverInstant &&
                  <FormattedMessage id="label.deliver-instantly"/>
                }
                {showDeliverFuture &&
                  intl.formatMessage({id: 'label.deliver-on'}) + ' ' + moment(deliveryDateTime).format('MMMM Do YYYY, h:mm a')
                }
              </Typography>
              <Typography variant={'h2'}>
                {formatMoney(giftAmount, currencyCode)}
              </Typography>
              {lineItems.map((lineItem) => (
                <Typography variant={'subtitle1'}>
                  <div style={{display: 'flex'}}>
                    {lineItem.key}
                    <div style={{flexGrow: 1}}/>
                    {lineItem.value}
                  </div>
                </Typography>
              ))}
              <Typography variant={'subtitle1'} color={'primary'} className={classes.amountYouPay}>
                <div style={{display: 'flex'}}>
                  <FormattedMessage id={'label.amount-you-pay'}/>
                  <div style={{flexGrow: 1}}/>
                  {formatMoney(amountDue.amount, amountDue.currencyCode)}
                </div>
              </Typography>
              <Divider className={classes.orderSummaryDivider}/>
              {action === CheckoutActions.NEW_CARD &&
                <>
                  <Typography variant={'subtitle2'}>
                    {sendToSender ? senderName : recipientName}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {sendToSender ? senderEmail : recipientEmail}
                  </Typography>
                </>
              }
            </Grid>
            {action === CheckoutActions.NEW_CARD && message &&
              <Grid item xs={12}>
                <Typography variant={'subtitle1'}>
                  &quot;{message}&quot;
                </Typography>
              </Grid>
            }
            {reloadNewBalance &&
              <Grid item xs={12}>
                <Typography variant={'subtitle1'}>
                  <FormattedMessage id={'label.reload-new-balance'} values={{newBalance: reloadNewBalance}}/>
                </Typography>
              </Grid>
            }
          </Grid>
        </StyleableSection>
      </>
    );
  }

  function TermsInForm() {
    if (checkoutSettings.loopzFooterDisabled) {
      return null;
    }
    return (
      <div className={classes.termsContainer}>
        <Typography variant="caption" className={classes.termsCentered}>
          <FormattedMessage id={'label.powered-by'}/>
          <Link href={'https://www.loopz.io'} target="_blank" className={classes.link}>Loopz Gift Cards</Link>
        </Typography>
        <Typography variant="caption" className={classes.termsCentered}>
          <Link href={'https://www.loopz.io/terms-of-use'} target="_blank" className={classes.link}><FormattedMessage
            id={'label.terms'}/></Link>
          <Link href={'https://www.loopz.io/privacy-policy'} target="_blank" className={classes.link}><FormattedMessage
            id={'label.privacy'}/></Link>
        </Typography>
      </div>
    );
  }

  const formContainerClassName = visible ? classes.formContainer : classnames(classes.formContainer, classes.hidden);

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={12} style={{display: 'flex', justifyContent: 'center'}}>
          <StyleableSection section={checkoutSettings.form} className={formContainerClassName}>
            <div className={classes.form}>
              <div className={classes.title}>
                <Typography variant="h2">
                  <FormattedMessage id={"header.checkout"}/>
                </Typography>
                <Typography variant={'h5'}>
                  <FormattedMessage id={"header.billing-details"}/>
                </Typography>
              </div>

              <Grid container direction={'row'} spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderOrderSummary()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    {paymentErrors.hasError('payment') &&
                    <Grid item xs={12}>
                      <div className={classes.errorBanner}>
                        <div className={classes.errorBannerText} data-test={'payment-error'}>
                          {paymentErrors.getError('payment')}
                        </div>
                      </div>
                    </Grid>
                    }
                    <Grid item xs={12}>
                      <>
                        {(cloverPaymentConfig && !cloverPaymentConfig.useDevPay) &&
                        <CloverCardDetailsField
                          cloverMerchantId={cloverPaymentConfig.cloverMerchantId}
                          apiKey={cloverPaymentConfig.cloverMerchantApiKey}
                          initializingPaymentConfig={initializingCloverPaymentConfig}
                          fullWidth={true}
                          errorText={errors.getError('card') && <FormattedMessage id={errors.getError('card')}/>}
                          showZip={currencyCode === 'USD'}
                          amountDue={amountDue}
                          idempotencyKey={idempotencyKey}
                          data-test={'clover-carddetails'}
                          onPaymentTokenCreated={handleCloverPaymentTokenCreated}
                        />
                        }
                        {(cloverPaymentConfig && cloverPaymentConfig.useDevPay) &&
                        <CloverDevPayCardDetailsForm
                          cloverMerchantId={cloverPaymentConfig.cloverMerchantId}
                          exponent={cloverPaymentConfig.exponent}
                          modulus={cloverPaymentConfig.modulus}
                          prefix={cloverPaymentConfig.prefix}
                          initializingPaymentConfig={initializingCloverPaymentConfig}
                          fullWidth={true}
                          errorText={errors.getError('card') && <FormattedMessage id={errors.getError('card')}/>}
                          showZip={currencyCode === 'USD'}
                          amountDue={amountDue}
                          idempotencyKey={idempotencyKey}
                          data-test={'clover-carddetails'}
                          onPaymentTokenCreated={handleCloverPaymentTokenCreated}
                        />
                        }
                        {squarePaymentConfig &&
                          <SquareCardDetailsForm
                              squareAppId={squarePaymentConfig.appId}
                              squareMerchantId={squarePaymentConfig.merchantId}
                              squareLocationId={squarePaymentConfig.locationId}
                              initializingPaymentConfig={initializingSquarePaymentConfig}
                              amountDue={amountDue}
                              idempotencyKey={idempotencyKey}
                              processing={paying}
                              onPaymentTokenCreated={handleSquarePaymentTokenCreated}
                          />
                        }
                      </>
                    </Grid>
                    {testMode &&
                    <Grid item xs={12} className={classes.center}>
                      {renderTestCardDetails()}
                    </Grid>
                    }
                    <Grid item xs={12} className={classes.center}>
                      <TermsInForm/>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </StyleableSection>
        </Grid>
      </Grid>
    </>
  );

}

export default injectIntl(withStyles(styles)(PaymentForm));
